import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { RichTextEditor } from '../components/RichTextEditor';
import { Scratchpad } from '../components/Scratchpad';
export const NotemannPage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    // State management
    const [folders, setFolders] = useState([]);
    const [notes, setNotes] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedNote, setSelectedNote] = useState(null);
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [isCreatingNote, setIsCreatingNote] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newNoteTitle, setNewNoteTitle] = useState('');
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showNavigationDropdown, setShowNavigationDropdown] = useState(false);
    const [showScratchpad, setShowScratchpad] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Edit/Delete state
    const [editingFolder, setEditingFolder] = useState(null);
    const [editingNote, setEditingNote] = useState(null);
    const [editFolderName, setEditFolderName] = useState('');
    const [editNoteTitle, setEditNoteTitle] = useState('');
    // Auto-save debouncing and state
    const saveTimeoutRef = useRef(null);
    const lastSaveRef = useRef('');
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState('');
    // Debounced save function
    const debouncedSave = useCallback((noteId, title, content) => {
        // Clear previous timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        // Only save if content actually changed
        const contentString = JSON.stringify({ noteId, title, content });
        if (contentString === lastSaveRef.current) {
            return;
        }
        // Set new timeout
        saveTimeoutRef.current = setTimeout(async () => {
            setIsSaving(true);
            try {
                const response = await fetch(`/api/notes/${noteId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ title, content })
                });
                if (response.ok) {
                    lastSaveRef.current = contentString;
                    setLastSaved(new Date().toLocaleTimeString());
                    // Update local note state to keep it in sync
                    if (selectedNote && selectedNote.id === noteId) {
                        setSelectedNote(prev => prev ? { ...prev, content, updated_at: Math.floor(Date.now() / 1000) } : null);
                    }
                    // Update notes list to reflect the change
                    setNotes(prev => prev.map(note => note.id === noteId
                        ? { ...note, content, updated_at: Math.floor(Date.now() / 1000) }
                        : note));
                    // console.log('Auto-saved note:', noteId);
                }
                else {
                    throw new Error('Save failed');
                }
            }
            catch (error) {
                console.error('Error auto-saving note:', error);
                setError('Failed to auto-save note');
            }
            finally {
                setIsSaving(false);
            }
        }, 1000); // 1 second delay
    }, [selectedNote]);
    // Manual save function
    const handleManualSave = useCallback(async (content) => {
        if (!selectedNote)
            return;
        setIsSaving(true);
        try {
            const response = await fetch(`/api/notes/${selectedNote.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ title: selectedNote.title, content })
            });
            if (response.ok) {
                const contentString = JSON.stringify({ noteId: selectedNote.id, title: selectedNote.title, content });
                lastSaveRef.current = contentString;
                setLastSaved(new Date().toLocaleTimeString());
                // Update local note state
                setSelectedNote(prev => prev ? { ...prev, content, updated_at: Math.floor(Date.now() / 1000) } : null);
                // Update notes list
                setNotes(prev => prev.map(note => note.id === selectedNote.id
                    ? { ...note, content, updated_at: Math.floor(Date.now() / 1000) }
                    : note));
                // console.log('Manually saved note:', selectedNote.id);
            }
            else {
                throw new Error('Save failed');
            }
        }
        catch (error) {
            console.error('Error manually saving note:', error);
            setError('Failed to save note');
        }
        finally {
            setIsSaving(false);
        }
    }, [selectedNote]);
    // Handle editor content changes
    const handleEditorChange = useCallback((content) => {
        if (selectedNote) {
            debouncedSave(selectedNote.id, selectedNote.title, content);
        }
    }, [selectedNote, debouncedSave]);
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);
    // Load folders and notes
    useEffect(() => {
        loadFolders();
        loadNotes();
    }, []);
    // Load notes when folder selection changes
    useEffect(() => {
        loadNotes();
    }, [selectedFolder]);
    const loadFolders = useCallback(async () => {
        try {
            const response = await fetch('/api/notes/folders', {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setFolders(data.folders || []);
            }
        }
        catch (error) {
            console.error('Error loading folders:', error);
        }
    }, []);
    const loadNotes = useCallback(async () => {
        try {
            const url = selectedFolder
                ? `/api/notes/notes?folder_id=${selectedFolder}`
                : '/api/notes/notes';
            const response = await fetch(url, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setNotes(data.notes || []);
            }
        }
        catch (error) {
            console.error('Error loading notes:', error);
        }
    }, [selectedFolder]);
    const handleCreateFolder = useCallback(async () => {
        if (!newFolderName.trim())
            return;
        setLoading(true);
        try {
            const response = await fetch('/api/notes/folders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: newFolderName.trim() })
            });
            if (response.ok) {
                setNewFolderName('');
                setIsCreatingFolder(false);
                await loadFolders();
            }
            else {
                const data = await response.json();
                setError(data.error || 'Failed to create folder');
            }
        }
        catch (error) {
            setError('Failed to create folder');
        }
        finally {
            setLoading(false);
        }
    }, [newFolderName, loadFolders]);
    const handleCreateNote = useCallback(async () => {
        if (!newNoteTitle.trim())
            return;
        setLoading(true);
        try {
            const response = await fetch('/api/notes/notes', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    title: newNoteTitle.trim(),
                    content: '{}',
                    folder_id: selectedFolder
                })
            });
            if (response.ok) {
                const data = await response.json();
                setNewNoteTitle('');
                setIsCreatingNote(false);
                await loadNotes();
                setSelectedNote(data.note);
            }
            else {
                const data = await response.json();
                setError(data.error || 'Failed to create note');
            }
        }
        catch (error) {
            setError('Failed to create note');
        }
        finally {
            setLoading(false);
        }
    }, [newNoteTitle, selectedFolder, loadNotes]);
    const handleEditFolder = useCallback((folder) => {
        setEditingFolder(folder.id);
        setEditFolderName(folder.name);
    }, []);
    const handleSaveFolderEdit = useCallback(async (folderId) => {
        if (!editFolderName.trim())
            return;
        try {
            const response = await fetch(`/api/notes/folders/${folderId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: editFolderName.trim() })
            });
            if (response.ok) {
                setEditingFolder(null);
                setEditFolderName('');
                await loadFolders();
            }
            else {
                const data = await response.json();
                setError(data.error || 'Failed to update folder');
            }
        }
        catch (error) {
            setError('Failed to update folder');
        }
    }, [editFolderName, loadFolders]);
    const handleDeleteFolder = useCallback(async (folderId) => {
        if (!confirm('Are you sure you want to delete this folder? This will also delete all notes in it.')) {
            return;
        }
        try {
            const response = await fetch(`/api/notes/folders/${folderId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (response.ok) {
                if (selectedFolder === folderId) {
                    setSelectedFolder(null);
                }
                await loadFolders();
                await loadNotes();
            }
            else {
                const data = await response.json();
                setError(data.error || 'Failed to delete folder');
            }
        }
        catch (error) {
            setError('Failed to delete folder');
        }
    }, [selectedFolder, loadFolders, loadNotes]);
    const handleEditNote = useCallback((note) => {
        setEditingNote(note.id);
        setEditNoteTitle(note.title);
    }, []);
    const handleSaveNoteEdit = useCallback(async (noteId) => {
        if (!editNoteTitle.trim())
            return;
        try {
            const response = await fetch(`/api/notes/${noteId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ title: editNoteTitle.trim() })
            });
            if (response.ok) {
                setEditingNote(null);
                setEditNoteTitle('');
                await loadNotes();
                // Update selected note title if it's the one being edited
                if (selectedNote?.id === noteId) {
                    setSelectedNote({ ...selectedNote, title: editNoteTitle.trim() });
                }
            }
            else {
                const data = await response.json();
                setError(data.error || 'Failed to update note');
            }
        }
        catch (error) {
            setError('Failed to update note');
        }
    }, [editNoteTitle, loadNotes, selectedNote]);
    const handleDeleteNote = useCallback(async (noteId) => {
        if (!confirm('Are you sure you want to delete this note?')) {
            return;
        }
        try {
            const response = await fetch(`/api/notes/${noteId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (response.ok) {
                if (selectedNote?.id === noteId) {
                    setSelectedNote(null);
                }
                await loadNotes();
            }
            else {
                const data = await response.json();
                setError(data.error || 'Failed to delete note');
            }
        }
        catch (error) {
            setError('Failed to delete note');
        }
    }, [selectedNote, loadNotes]);
    const handleLogout = useCallback(() => {
        logout();
        setShowUserDropdown(false);
    }, [logout]);
    // Get user display name and initial
    const userDisplayName = user?.email || 'User';
    const userInitial = userDisplayName.charAt(0).toUpperCase();
    return (_jsxs("div", { className: "h-screen flex bg-gray-50", children: [_jsxs("div", { className: "w-80 flex flex-col bg-white shadow-sm", children: [_jsx("div", { className: "p-4 border-b border-gray-100", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-purple-600", children: "Multiwise" }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "Documentation & Notes" })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { onClick: () => setShowScratchpad(true), className: "p-2 text-purple-600 hover:text-purple-700 transition-colors rounded-md hover:bg-purple-50", title: "Open Personal Scratchpad", children: _jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" }) }) }), _jsxs("div", { className: "relative", children: [_jsx("button", { onClick: () => setShowNavigationDropdown(!showNavigationDropdown), className: "p-2 text-gray-600 hover:text-purple-600 transition-colors rounded-md hover:bg-purple-50", title: "Navigation Menu", children: _jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" }) }) }), showNavigationDropdown && (_jsxs("div", { className: "absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200", children: [_jsxs("a", { href: "/", onClick: (e) => {
                                                                if (!e.ctrlKey && !e.metaKey) {
                                                                    e.preventDefault();
                                                                    navigate('/');
                                                                }
                                                                setShowNavigationDropdown(false);
                                                            }, className: "flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100", children: [_jsx("svg", { className: "w-4 h-4 mr-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" }) }), "AI Compare"] }), _jsxs("a", { href: "/superintelligent", onClick: (e) => {
                                                                if (!e.ctrlKey && !e.metaKey) {
                                                                    e.preventDefault();
                                                                    navigate('/superintelligent');
                                                                }
                                                                setShowNavigationDropdown(false);
                                                            }, className: "flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100", children: [_jsx("svg", { className: "w-4 h-4 mr-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M13 10V3L4 14h7v7l9-11h-7z" }) }), "Synthesizer"] }), _jsx("a", { href: "/superintelligent-message", onClick: (e) => {
                                                                if (!e.ctrlKey && !e.metaKey) {
                                                                    e.preventDefault();
                                                                    navigate('/superintelligent-message');
                                                                }
                                                                setShowNavigationDropdown(false);
                                                            }, className: "flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100", children: "\uD83D\uDC7B \u00A0Ghostwriter" }), _jsxs("a", { href: "/brand-profiles", onClick: (e) => {
                                                                if (!e.ctrlKey && !e.metaKey) {
                                                                    e.preventDefault();
                                                                    navigate('/brand-profiles');
                                                                }
                                                                setShowNavigationDropdown(false);
                                                            }, className: "flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100", children: [_jsx("svg", { className: "w-4 h-4 mr-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" }) }), "My Brands"] })] }))] }), _jsxs("div", { className: "relative", children: [_jsx("button", { onClick: () => setShowUserDropdown(!showUserDropdown), className: "w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center hover:bg-purple-200 transition-colors", title: "Profile Menu", children: _jsx("span", { className: "text-sm font-medium text-purple-700", children: userInitial }) }), showUserDropdown && (_jsxs("div", { className: "absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10", children: [_jsx("a", { href: "/settings", onClick: (e) => {
                                                                if (!e.ctrlKey && !e.metaKey) {
                                                                    e.preventDefault();
                                                                    navigate('/settings');
                                                                }
                                                                setShowUserDropdown(false);
                                                            }, className: "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100", children: "Settings" }), _jsxs("a", { href: "/billing", onClick: (e) => {
                                                                if (!e.ctrlKey && !e.metaKey) {
                                                                    e.preventDefault();
                                                                    navigate('/billing');
                                                                }
                                                                setShowUserDropdown(false);
                                                            }, className: "flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100", children: [_jsx("svg", { className: "w-4 h-4 mr-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" }) }), "Billing"] }), _jsxs("button", { onClick: handleLogout, className: "flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100", children: [_jsx("svg", { className: "w-4 h-4 mr-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013 3v1" }) }), "Logout"] })] }))] })] })] }) }), _jsxs("div", { className: "flex-1 flex flex-col overflow-hidden", children: [_jsxs("div", { className: "h-1/2 border-b border-gray-100 flex flex-col", children: [_jsxs("div", { className: "p-4 border-b border-gray-100 flex-shrink-0", children: [_jsxs("div", { className: "flex items-center justify-between mb-3", children: [_jsx("h3", { className: "text-sm font-medium text-gray-900", children: "Folders" }), _jsx("button", { onClick: () => setIsCreatingFolder(true), className: "text-sm text-purple-600 hover:text-purple-700", children: "+ New" })] }), isCreatingFolder && (_jsxs("div", { className: "mb-3", children: [_jsx("input", { type: "text", value: newFolderName, onChange: (e) => setNewFolderName(e.target.value), placeholder: "Folder name", className: "w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500", onKeyDown: (e) => {
                                                            if (e.key === 'Enter')
                                                                handleCreateFolder();
                                                            if (e.key === 'Escape') {
                                                                setIsCreatingFolder(false);
                                                                setNewFolderName('');
                                                            }
                                                        }, autoFocus: true }), _jsxs("div", { className: "flex gap-1 mt-2", children: [_jsx("button", { onClick: handleCreateFolder, disabled: !newFolderName.trim() || loading, className: "text-xs px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50", children: "Create" }), _jsx("button", { onClick: () => {
                                                                    setIsCreatingFolder(false);
                                                                    setNewFolderName('');
                                                                }, className: "text-xs px-2 py-1 text-gray-600 hover:text-gray-800", children: "Cancel" })] })] }))] }), _jsx("div", { className: "flex-1 overflow-y-auto p-4", children: _jsxs("div", { className: "space-y-1", children: [_jsx("button", { onClick: () => setSelectedFolder(null), className: `w-full text-left text-sm px-2 py-1 rounded transition-colors ${selectedFolder === null
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : 'text-gray-700 hover:bg-gray-100'}`, children: "\uD83D\uDCC4 All Notes" }), folders.map((folder) => (_jsx("div", { className: `group relative rounded transition-colors ${selectedFolder === folder.id
                                                        ? 'bg-purple-100'
                                                        : 'hover:bg-gray-100'}`, children: editingFolder === folder.id ? (_jsx("div", { className: "p-1", children: _jsx("input", { type: "text", value: editFolderName, onChange: (e) => setEditFolderName(e.target.value), className: "w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500", onKeyDown: (e) => {
                                                                if (e.key === 'Enter')
                                                                    handleSaveFolderEdit(folder.id);
                                                                if (e.key === 'Escape') {
                                                                    setEditingFolder(null);
                                                                    setEditFolderName('');
                                                                }
                                                            }, onBlur: () => handleSaveFolderEdit(folder.id), autoFocus: true }) })) : (_jsxs(_Fragment, { children: [_jsxs("button", { onClick: () => setSelectedFolder(folder.id), className: `w-full text-left text-sm px-2 py-1 transition-colors ${selectedFolder === folder.id
                                                                    ? 'text-purple-700'
                                                                    : 'text-gray-700'}`, children: ["\uD83D\uDCC1 ", folder.name] }), _jsxs("div", { className: "absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity", children: [_jsx("button", { onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            handleEditFolder(folder);
                                                                        }, className: "text-xs text-gray-500 hover:text-gray-700 px-1", title: "Edit folder", children: "\u270F\uFE0F" }), _jsx("button", { onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteFolder(folder.id);
                                                                        }, className: "text-xs text-gray-500 hover:text-red-600 px-1", title: "Delete folder", children: "\uD83D\uDDD1\uFE0F" })] })] })) }, folder.id)))] }) })] }), _jsxs("div", { className: "h-1/2 flex flex-col", children: [_jsxs("div", { className: "p-4 border-b border-gray-100 flex-shrink-0", children: [_jsxs("div", { className: "flex items-center justify-between mb-3", children: [_jsx("h3", { className: "text-sm font-medium text-gray-900", children: "Notes" }), _jsx("button", { onClick: () => setIsCreatingNote(true), className: "text-sm text-purple-600 hover:text-purple-700", children: "+ New" })] }), isCreatingNote && (_jsxs("div", { className: "mb-3", children: [_jsx("input", { type: "text", value: newNoteTitle, onChange: (e) => setNewNoteTitle(e.target.value), placeholder: "Note title", className: "w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500", onKeyDown: (e) => {
                                                            if (e.key === 'Enter')
                                                                handleCreateNote();
                                                            if (e.key === 'Escape') {
                                                                setIsCreatingNote(false);
                                                                setNewNoteTitle('');
                                                            }
                                                        }, autoFocus: true }), _jsxs("div", { className: "flex gap-1 mt-2", children: [_jsx("button", { onClick: handleCreateNote, disabled: !newNoteTitle.trim() || loading, className: "text-xs px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50", children: "Create" }), _jsx("button", { onClick: () => {
                                                                    setIsCreatingNote(false);
                                                                    setNewNoteTitle('');
                                                                }, className: "text-xs px-2 py-1 text-gray-600 hover:text-gray-800", children: "Cancel" })] })] }))] }), _jsx("div", { className: "flex-1 overflow-y-auto p-4", children: _jsx("div", { className: "space-y-1", children: notes.map((note) => (_jsx("div", { className: `group relative rounded transition-colors ${selectedNote?.id === note.id
                                                    ? 'bg-purple-100'
                                                    : 'hover:bg-gray-100'}`, children: editingNote === note.id ? (_jsx("div", { className: "p-1", children: _jsx("input", { type: "text", value: editNoteTitle, onChange: (e) => setEditNoteTitle(e.target.value), className: "w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500", onKeyDown: (e) => {
                                                            if (e.key === 'Enter')
                                                                handleSaveNoteEdit(note.id);
                                                            if (e.key === 'Escape') {
                                                                setEditingNote(null);
                                                                setEditNoteTitle('');
                                                            }
                                                        }, onBlur: () => handleSaveNoteEdit(note.id), autoFocus: true }) })) : (_jsxs(_Fragment, { children: [_jsxs("button", { onClick: () => setSelectedNote(note), className: `w-full text-left text-sm px-2 py-2 transition-colors ${selectedNote?.id === note.id
                                                                ? 'text-purple-700'
                                                                : 'text-gray-700'}`, children: [_jsx("div", { className: "font-medium truncate", children: note.title }), _jsx("div", { className: "text-xs text-gray-500 truncate mt-1", children: new Date(note.updated_at * 1000).toLocaleDateString() })] }), _jsxs("div", { className: "absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity", children: [_jsx("button", { onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        handleEditNote(note);
                                                                    }, className: "text-xs text-gray-500 hover:text-gray-700 px-1", title: "Edit note title", children: "\u270F\uFE0F" }), _jsx("button", { onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteNote(note.id);
                                                                    }, className: "text-xs text-gray-500 hover:text-red-600 px-1", title: "Delete note", children: "\uD83D\uDDD1\uFE0F" })] })] })) }, note.id))) }) })] })] })] }), _jsxs("div", { className: "flex-1 flex flex-col min-w-0", children: [_jsx("div", { className: "bg-white border-b border-gray-200 px-6 py-4", children: _jsx("div", { className: "flex items-center justify-between", children: _jsx("h2", { className: "text-lg font-medium text-gray-900", children: selectedNote ? selectedNote.title : 'Select a note to edit' }) }) }), _jsx("div", { className: "flex-1 overflow-hidden", children: selectedNote ? (_jsx("div", { className: "h-full p-6", children: _jsx("div", { className: "h-full border border-gray-300 rounded-lg overflow-hidden", children: _jsx(RichTextEditor, { content: selectedNote.content || '', onChange: handleEditorChange, onManualSave: handleManualSave, placeholder: "Start writing your note...", className: "h-full", isSaving: isSaving, lastSaved: lastSaved }) }) })) : (_jsx("div", { className: "h-full flex items-center justify-center", children: _jsxs("div", { className: "text-center", children: [_jsx("svg", { className: "w-16 h-16 text-gray-300 mx-auto mb-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" }) }), _jsx("h3", { className: "text-lg font-medium text-gray-900 mb-2", children: "Your Notes Workspace" }), _jsx("p", { className: "text-gray-500 max-w-md mx-auto", children: "Create folders to organize your notes and start writing to capture your thoughts and research." })] }) })) })] }), error && (_jsx("div", { className: "fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg", children: _jsxs("div", { className: "flex items-center", children: [_jsx("svg", { className: "h-5 w-5 text-red-500 mr-2", fill: "none", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" }) }), _jsx("span", { className: "text-red-700", children: error }), _jsx("button", { onClick: () => setError(''), className: "ml-3 text-red-600 hover:text-red-800", children: "\u00D7" })] }) })), _jsx(Scratchpad, { isOpen: showScratchpad, onClose: () => setShowScratchpad(false) })] }));
};
